import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Button,
  MenuItem,
} from '@mui/material';
import { GlCategoryService, FsCategoryService } from '@/services/accountService';
import { usePageCrud } from '@/hooks/usePageCrud';

interface Props {
  open: boolean;
  onClose: (payload?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => void;
  categoryId?: number;
}

type FsOption = { code: string; label: string };

const emptyForm = (): GlCategoryService.GlCategory => ({
  code: '',
  nameEn: '',
  nameZh: '',
  type: undefined,
  parentId: null,
  fsCode: undefined,
  remark: '',
});

const GlCategoryDetail: React.FC<Props> = ({ open, onClose, categoryId }) => {
  const isEdit = !!categoryId;
  const { canCreate, canUpdate } = usePageCrud({
    read: ['gl_category_get', 'gl_category_list'],
    create: 'account:gl_category:create',
    update: 'account:gl_category:update',
    remove: 'account:gl_category:delete',
  });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<GlCategoryService.GlCategory>(emptyForm());
  const [fieldErrors, setFieldErrors] = useState<{
    code?: string;
    type?: string;
    nameEn?: string;
    nameZh?: string;
    parentId?: string;
    fsCode?: string;
    _global?: string;
  }>({});
  const [fsOptions, setFsOptions] = useState<FsOption[]>([]);

  const loadFsOptions = async () => {
    try {
      const map = await FsCategoryService.queryFsCategorySelection();
      const opts = Object.entries(map || {})
        .filter(([code]) => !!code)
        .map(([code, nameEn]) => ({
          code,
          label: nameEn ? `${code} — ${nameEn}` : code,
        }))
        .sort((a, b) => a.code.localeCompare(b.code));
      setFsOptions(opts);
    } catch {
      setFsOptions([]);
    }
  };

  const loadDetail = async () => {
    if (!categoryId) return;
    try {
      setForm(await GlCategoryService.getCategory(categoryId));
    } catch {
      /* keep form */
    }
  };

  useEffect(() => {
    if (!open) return;
    loadFsOptions();
    if (isEdit) loadDetail();
    else setForm(emptyForm());
    setFieldErrors({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categoryId]);

  const selectedFs = useMemo(
    () => fsOptions.find((o) => o.code === (form.fsCode || '').trim()) ?? null,
    [fsOptions, form.fsCode],
  );

  const onSave = async () => {
    setSaving(true);
    try {
      const payload: GlCategoryService.GlCategory = {
        ...form,
        fsCode: form.fsCode?.trim() || undefined,
      };
      if (isEdit && form.categoryId) {
        await GlCategoryService.updateCategory(form.categoryId, payload);
      } else {
        await GlCategoryService.createCategory(payload);
      }
      onClose({ refreshed: true, message: 'Saved successfully', severity: 'success' });
    } catch (e: any) {
      const msg = e?.message || 'Save failed';
      const fe: typeof fieldErrors = { _global: msg };
      const lower = String(msg).toLowerCase();
      if (lower.includes('code')) fe.code = msg;
      if (lower.includes('type')) fe.type = msg;
      if (lower.includes('parent')) fe.parentId = msg;
      if (lower.includes('fs')) fe.fsCode = msg;
      setFieldErrors(fe);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit GL Category' : 'Create GL Category'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            size="small"
            label="Code"
            required
            error={!form.code || !!fieldErrors.code}
            helperText={fieldErrors.code || (!form.code ? 'Code is required' : '')}
            value={form.code || ''}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />
          <TextField
            size="small"
            label="Name (EN)"
            value={form.nameEn || ''}
            onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
          />
          <TextField
            size="small"
            label="Name (ZH)"
            value={form.nameZh || ''}
            onChange={(e) => setForm({ ...form, nameZh: e.target.value })}
          />
          <TextField
            select
            size="small"
            label="Type"
            required
            error={!form.type || !!fieldErrors.type}
            helperText={fieldErrors.type || (!form.type ? 'Type is required' : '')}
            value={form.type || ''}
            onChange={(e) => setForm({ ...form, type: e.target.value as any })}
          >
            <MenuItem value="ASSET">ASSET</MenuItem>
            <MenuItem value="LIABILITY">LIABILITY</MenuItem>
            <MenuItem value="EQUITY">EQUITY</MenuItem>
            <MenuItem value="REVENUE">REVENUE</MenuItem>
            <MenuItem value="EXPENSE">EXPENSE</MenuItem>
            <MenuItem value="ADJUSTMENT">ADJUSTMENT</MenuItem>
          </TextField>
          <Autocomplete
            size="small"
            options={fsOptions}
            value={selectedFs}
            onChange={(_, opt) => setForm({ ...form, fsCode: opt?.code || undefined })}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            renderInput={(params) => (
              <TextField
                {...params}
                label="FS Code"
                error={!!fieldErrors.fsCode}
                helperText={fieldErrors.fsCode || 'Maps this GL leaf to FS report line (e.g. BS-CA-CASH)'}
              />
            )}
          />
          <TextField
            size="small"
            label="Parent ID"
            error={!!fieldErrors.parentId}
            helperText={fieldErrors.parentId || ''}
            value={form.parentId ?? ''}
            onChange={(e) => setForm({
              ...form,
              parentId: e.target.value === '' ? undefined : Number(e.target.value),
            })}
          />
          <TextField
            size="small"
            label="Remark"
            value={form.remark || ''}
            onChange={(e) => setForm({ ...form, remark: e.target.value })}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        {(isEdit ? canUpdate : canCreate) && (
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving || !form.code || !form.type}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GlCategoryDetail;
